import { animate, ValueAnimationTransition } from 'motion';
import { useMotionValue } from 'motion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useWindowSize } from 'react-use';

import { usePrevious } from '../../hooks';

import { DIRECTION, DRAWER_SIZE } from './SwipeableDrawer';

interface UseSwipeableDrawerArgs {
  isOpen?: boolean;
  onClose?: () => void;
  hasBackdropScroll?: boolean;
  direction: DIRECTION;
  size?: DRAWER_SIZE;
}

export const useSwipeableDrawer = ({
  isOpen,
  onClose,
  hasBackdropScroll = true,
  direction = DIRECTION.TOP,
  size = DRAWER_SIZE.SMALL,
}: UseSwipeableDrawerArgs) => {
  const previousIsOpen = usePrevious(isOpen);
  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const height = windowHeight + 100;
  const width =
    size === DRAWER_SIZE.FULL_SCREEN
      ? windowWidth
      : Math.min(size, windowWidth);

  const x = useMotionValue(
    direction === DIRECTION.LEFT
      ? width
      : direction === DIRECTION.RIGHT
        ? -width
        : 0,
  );
  const y = useMotionValue(direction === DIRECTION.TOP ? height : 0);

  const springOpen = useMemo(
    () => ({ type: 'spring', stiffness: 600, damping: 40 }),
    [],
  ) as ValueAnimationTransition<string>;
  const springRebound = useMemo(
    () => ({
      type: 'spring',
      stiffness: 800,
      damping: 30,
    }),
    [],
  ) as ValueAnimationTransition<string>;

  const open = useCallback(
    ({ canceled }: { canceled: boolean } = { canceled: false }) => {
      const options = canceled ? springRebound : springOpen;

      if (direction === DIRECTION.TOP) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        animate(y, 0, options);
      } else if (direction === DIRECTION.RIGHT) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        animate(x, 0, options);
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        animate(x, 0, options);
      }
    },
    [direction, springOpen, springRebound, x, y],
  );

  const close = useCallback(
    (velocity = 0) => {
      if (direction === DIRECTION.TOP) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const controls = animate(y, height, { ...springOpen, velocity });

        controls.finished.then(() => {
          if (onClose) {
            onClose();
          }
        });
      } else if (direction === DIRECTION.RIGHT) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const controls = animate(x, -width, { ...springOpen, velocity });

        controls.finished.then(() => {
          if (onClose) {
            onClose();
          }
        });
      } else if (direction === DIRECTION.LEFT) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const controls = animate(x, width, { ...springOpen, velocity });

        controls.finished.then(() => {
          if (onClose) {
            onClose();
          }
        });
      }
    },
    [direction, height, onClose, springOpen, width, x, y],
  );

  // Keep motion values in sync if direction/size changes
  useEffect(() => {
    if (direction === DIRECTION.TOP) {
      y.set(isOpen ? 0 : height);
      x.set(0);
    } else if (direction === DIRECTION.RIGHT) {
      x.set(isOpen ? 0 : -width);
      y.set(0);
    } else {
      x.set(isOpen ? 0 : width);
      y.set(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, height, width]);

  useEffect(() => {
    if (isOpen) {
      open();
      if (!hasBackdropScroll) {
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.body.style.overflow = 'visible';
      };
    }

    if (!isOpen && previousIsOpen === true) {
      if (!hasBackdropScroll) {
        document.body.style.overflow = 'visible';
      }
      close();
    }

    return () => {
      document.body.style.overflow = 'visible';
    };
  }, [close, hasBackdropScroll, isOpen, open, previousIsOpen]);

  const dragProps =
    direction === DIRECTION.TOP
      ? {
          drag: 'y' as const,
          dragElastic: 0.2,
          onDragEnd: (
            _e: unknown,
            info: {
              offset: { x: number; y: number };
              velocity: { x: number; y: number };
            },
          ) => {
            const my = info.offset.y;
            const vy = info.velocity.y;
            const shouldClose = my > windowHeight * 0.25;

            if (shouldClose) {
              close(vy);
            } else {
              open({ canceled: true });
            }
          },
        }
      : undefined;

  return {
    dragProps,
    display: 'block',
    width,
    height,
    y,
    x,
  } as const;
};
